/**
 * card-preset-gallery.js
 * Web Component <card-preset-gallery>
 *
 * Handles left/right arrow image navigation + dot indicators on preset product
 * cards across all pages (Home, All Presets, Bundle, Favorites).
 *
 * Design decisions:
 * - Self-contained — no pub/sub, no external events, no HTTP requests on navigate
 * - Lazy-initialised via IntersectionObserver (threshold 0.1) for CWV performance
 * - Wrap-around: last image → next → first image (modulo formula)
 * - Single-image cards: arrows/dots NOT rendered in Liquid; Web Component skips init
 * - Keyboard: ArrowLeft / ArrowRight when component (or child) has focus
 *
 * @see specs/007-in-card-image-nav/data-model.md
 */

class CardPresetGallery extends HTMLElement {
  /**
   * Called when the element is connected to the DOM.
   * Sets up IntersectionObserver for lazy initialisation.
   */
  connectedCallback() {
    this._slides = [];
    this._arrows = null;
    this._dots = [];
    this._total = 0;
    this._current = 0;
    this._observer = null;
    this._initialized = false;
    this._boundKeydown = this._onKeydown.bind(this);
    this._boundPreInitClick = this._handlePreInitClick.bind(this);
    this._boundPreInitPointerEnter = this._handlePreInitPointerEnter.bind(this);
    this._boundPreInitFocus = this._handlePreInitFocus.bind(this);

    // Ensure the component can receive keyboard events
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Fallback: ensure the gallery becomes interactive on first hover, focus, or click
    // even if IntersectionObserver hasn't fired yet.
    this.addEventListener('click', this._boundPreInitClick, true);
    this.addEventListener('pointerenter', this._boundPreInitPointerEnter, { once: true });
    this.addEventListener('focusin', this._boundPreInitFocus, { once: true });

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        this._onIntersect.bind(this),
        { threshold: 0.1 }
      );
      this._observer.observe(this);
    } else {
      // Fallback for browsers without IntersectionObserver
      this._initGallery();
    }
  }

  /**
   * Called when element is removed from the DOM. Cleans up observer.
   */
  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    this.removeEventListener('click', this._boundPreInitClick, true);
    this.removeEventListener('pointerenter', this._boundPreInitPointerEnter);
    this.removeEventListener('focusin', this._boundPreInitFocus);
    this.removeEventListener('keydown', this._boundKeydown);
  }

  /**
   * IntersectionObserver callback. Fires _initGallery once on first viewport entry.
   * @param {IntersectionObserverEntry[]} entries
   */
  _onIntersect(entries) {
    const entry = entries[0];
    if (entry.isIntersecting && !this._initialized) {
      this._observer.unobserve(this);
      this._initGallery();
    }
  }

  /**
   * Finds DOM elements, binds event listeners, and activates the gallery.
   * If only one image is present, hides arrows/dots and returns early.
   */
  _initGallery() {
    if (this._initialized) return;

    this._slides = Array.from(this.querySelectorAll('.card-gallery__slide'));
    this._total = this._slides.length;
    this._initialized = true;

    this.removeEventListener('click', this._boundPreInitClick, true);
    this.removeEventListener('pointerenter', this._boundPreInitPointerEnter);
    this.removeEventListener('focusin', this._boundPreInitFocus);

    // Safety guard: no gallery needed for 0 or 1 image
    if (this._total <= 1) {
      const arrowPrev = this.querySelector('.arrow.prev');
      const arrowNext = this.querySelector('.arrow.next');
      const dotsContainer = this.querySelector('.card-gallery__dots');
      if (arrowPrev) arrowPrev.style.display = 'none';
      if (arrowNext) arrowNext.style.display = 'none';
      if (dotsContainer) dotsContainer.style.display = 'none';
      return;
    }

    const arrowPrev = this.querySelector('.arrow.prev');
    const arrowNext = this.querySelector('.arrow.next');
    this._arrows = arrowPrev && arrowNext ? { prev: arrowPrev, next: arrowNext } : null;
    this._dots = Array.from(this.querySelectorAll('.card-gallery__dot'));

    // Determine initial active index from DOM (first .active slide)
    const activeIndex = this._slides.findIndex(s => s.classList.contains('active'));
    this._current = activeIndex >= 0 ? activeIndex : 0;

    // Bind arrow clicks
    if (this._arrows) {
      this._arrows.next.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._navigate(1);
      });
      this._arrows.prev.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._navigate(-1);
      });
    }

    // Bind dot clicks
    this._dots.forEach((dot, i) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._goTo(i);
      });
    });

    // Bind keyboard navigation
    this.addEventListener('keydown', this._boundKeydown);
  }

  /**
   * Initialise on first hover for users who interact before observer fires.
   */
  _handlePreInitPointerEnter() {
    this._initGallery();
  }

  /**
   * Initialise on first focus for keyboard users.
   */
  _handlePreInitFocus() {
    this._initGallery();
  }

  /**
   * Initialise on first click and execute the intended action immediately.
   * @param {MouseEvent} e
   */
  _handlePreInitClick(e) {
    if (this._initialized) return;

    const dot = e.target.closest('.card-gallery__dot');
    const nextArrow = e.target.closest('.arrow.next');
    const prevArrow = e.target.closest('.arrow.prev');

    this._initGallery();

    if (nextArrow) {
      e.preventDefault();
      e.stopPropagation();
      this._navigate(1);
      return;
    }

    if (prevArrow) {
      e.preventDefault();
      e.stopPropagation();
      this._navigate(-1);
      return;
    }

    if (dot) {
      e.preventDefault();
      e.stopPropagation();
      const index = Number(dot.dataset.index);
      if (!Number.isNaN(index)) {
        this._goTo(index);
      }
    }
  }

  /**
   * Advance or rewind the gallery by one step with wrap-around.
   * @param {-1|1} direction — 1 = forward, -1 = backward
   */
  _navigate(direction) {
    const prev = this._current;
    this._current = (this._current + direction + this._total) % this._total;
    this._updateDOM(prev, this._current);
  }

  /**
   * Jump directly to a specific image index.
   * @param {number} index — 0-based image index
   */
  _goTo(index) {
    if (index === this._current) return;
    const prev = this._current;
    this._current = index;
    this._updateDOM(prev, this._current);
  }

  /**
   * Toggle .active class on slides and dots (prev → off, next → on).
   * Lazy-loads deferred images (data-src) on first navigation to them.
   * @param {number} prev — previous active index
   * @param {number} next — new active index
   */
  _updateDOM(prev, next) {
    this._slides[prev].classList.remove('active');

    const nextSlide = this._slides[next];
    // Lazy-load image if it has a data-src (deferred loading)
    if (nextSlide.dataset.src) {
      nextSlide.src = nextSlide.dataset.src;
      if (nextSlide.dataset.srcset) {
        nextSlide.srcset = nextSlide.dataset.srcset;
      }
      delete nextSlide.dataset.src;
      delete nextSlide.dataset.srcset;
    }

    // Pre-load the next image in line (n+1) so it's ready when user navigates again
    const preloadIndex = (next + 1) % this._total;
    const preloadSlide = this._slides[preloadIndex];
    if (preloadSlide && preloadSlide.dataset.src) {
      preloadSlide.src = preloadSlide.dataset.src;
      if (preloadSlide.dataset.srcset) {
        preloadSlide.srcset = preloadSlide.dataset.srcset;
      }
      delete preloadSlide.dataset.src;
      delete preloadSlide.dataset.srcset;
    }

    nextSlide.classList.add('active');

    if (this._dots.length) {
      this._dots[prev].classList.remove('active');
      this._dots[next].classList.add('active');
    }
  }

  /**
   * Keyboard handler — ArrowLeft / ArrowRight cycle images.
   * Scoped to this component via target check.
   * @param {KeyboardEvent} e
   */
  _onKeydown(e) {
    if (!e.target.closest('card-preset-gallery')) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._navigate(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._navigate(-1);
    }
  }
}

customElements.define('card-preset-gallery', CardPresetGallery);
